/**
 * BattleRoomLayout.tsx (wrapper)
 * date_of_change: 2025-09-04
 * 
 * Provides the new UI layout system for the BattleRoom without breaking existing logic.
 * Wraps the existing LeftPanel, RightPanel, and Toolbar with our new layout components.
 */
import React, { useEffect, useRef, useState } from 'react';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import useBattleRoomCompactViewport from './useBattleRoomCompactViewport';
import './BattleRoomLayout.css';
import { 
  Swords, 
  Clock, 
  Dices,
  Settings, 
  Map, 
  Layers, 
  Grid,
  Upload,
  Download,
  ZoomIn,
  ZoomOut,
  BookOpen,
  MessageSquare,
  Scroll
} from 'lucide-react';
import { useDock } from './useDock';

// @date_of_change: 2025-09-12 - Removed unused Mode type alias to satisfy lint

interface BattleRoomLayoutProps {
  leftPanel: React.ReactNode;
  rightPanel: React.ReactNode; 
  topTools: React.ReactNode;
  topIconbar?: React.ReactNode;
  children: React.ReactNode; // The Stage/Canvas area
  // @date_of_change: 2025-09-12 - Optional callbacks for icon-rail actions (icon mode)
  onLeftIconAction?: (section: 'initiative' | 'actions' | 'gm') => void;
  onRightIconAction?: (section: 'sheet' | 'log' | 'chat') => void;
  openSidebarRequest?: { side: 'left' | 'right'; ts: number } | null;
}

export default function BattleRoomLayout({ 
  leftPanel, 
  rightPanel, 
  topTools, 
  topIconbar,
  children,
  onLeftIconAction,
  onRightIconAction,
  openSidebarRequest
}: BattleRoomLayoutProps) {
  const leftDock = useDock('sidebar:left', 'expanded');
  const rightDock = useDock('sidebar:right', 'expanded');
  const isCompactViewport = useBattleRoomCompactViewport();
  const [compactDrawer, setCompactDrawer] = useState<'left' | 'right' | null>(null);
  const handledSidebarRequestRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isCompactViewport) {
      setCompactDrawer(null);
    }
  }, [isCompactViewport]);

  useEffect(() => {
    if (!openSidebarRequest || handledSidebarRequestRef.current === openSidebarRequest.ts) {
      return;
    }
    handledSidebarRequestRef.current = openSidebarRequest.ts;
    if (isCompactViewport) {
      setCompactDrawer(openSidebarRequest.side);
      return;
    }
    if (openSidebarRequest.side === 'left') {
      leftDock.setMode('expanded');
    } else {
      rightDock.setMode('expanded');
    }
  }, [openSidebarRequest, isCompactViewport, leftDock, rightDock]);

  useEffect(() => {
    if (!compactDrawer) {
      return undefined;
    }

    const closeCompactDrawerOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setCompactDrawer(null);
      }
    };

    window.addEventListener('keydown', closeCompactDrawerOnEscape);
    return () => window.removeEventListener('keydown', closeCompactDrawerOnEscape);
  }, [compactDrawer]);
  
  const toggleLeftPanel = () => {
    if (isCompactViewport) {
      setCompactDrawer((currentDrawer) => currentDrawer === 'left' ? null : 'left');
      return;
    }
    leftDock.setMode(leftDock.mode === 'collapsed' ? 'expanded' : 'collapsed');
  };
  
  const toggleRightPanel = () => {
    if (isCompactViewport) {
      setCompactDrawer((currentDrawer) => currentDrawer === 'right' ? null : 'right');
      return;
    }
    rightDock.setMode(rightDock.mode === 'collapsed' ? 'expanded' : 'collapsed');
  };

  const leftMode = isCompactViewport
    ? (compactDrawer === 'left' ? 'expanded' : 'collapsed')
    : leftDock.mode;
  const rightMode = isCompactViewport
    ? (compactDrawer === 'right' ? 'expanded' : 'collapsed')
    : rightDock.mode;

  // One-time layout migration to place the former left panel on the right
  useEffect(() => {
    try {
      const MIGRATION_KEY = 'layout:migrated:sidebar-swap:2025-09-04-v2';
      if (!localStorage.getItem(MIGRATION_KEY)) {
        leftDock.setMode('expanded');
        rightDock.setMode('expanded');
        localStorage.setItem(MIGRATION_KEY, '1');
      }
    } catch (e) {
      // ignore storage errors
    }
  }, [leftDock, rightDock]);

  // Left sidebar icons representing main sections
  const leftIcons = (
    <div className="flex flex-col gap-4 items-center p-2">
      {/* Initiative Tracker */}
      <button 
        className="battle-room-layout__rail-button btn-ghost w-10 h-10 p-0 flex items-center justify-center rounded-lg transition-colors"
        title="Initiative Tracker"
        aria-label="Initiative Tracker"
        onClick={() => {
          leftDock.setMode('expanded');
          // @date_of_change: 2025-09-12 - notify parent to focus section
          onLeftIconAction && onLeftIconAction('initiative');
        }}
      >
        <Clock size={20} className="text-base-content/80" />
      </button>
      
      {/* Actions */}
      <button 
        className="battle-room-layout__rail-button btn-ghost w-10 h-10 p-0 flex items-center justify-center rounded-lg transition-colors"
        title="Actions"
        aria-label="Actions"
        onClick={() => {
          leftDock.setMode('expanded');
          onLeftIconAction && onLeftIconAction('actions');
        }}
      >
        <Swords size={20} className="text-base-content/80" />
      </button>
      
      {/* GM Tools */}
      <button 
        className="battle-room-layout__rail-button btn-ghost w-10 h-10 p-0 flex items-center justify-center rounded-lg transition-colors"
        title="GM Tools"
        aria-label="GM Tools"
        onClick={() => {
          leftDock.setMode('expanded');
          onLeftIconAction && onLeftIconAction('gm');
        }}
      >
        <Dices size={20} className="text-base-content/80" />
      </button>
    </div>
  );

  // Compact toolbar icons for when topbar is collapsed
  const defaultIconbar = (
    <div className="flex gap-2 items-center">
      <button className="btn-ghost w-8 h-8 p-0" title="Grid Toggle">
        <Grid size={16} />
      </button>
      <button className="btn-ghost w-8 h-8 p-0" title="Layers">
        <Layers size={16} />
      </button>
      <button className="btn-ghost w-8 h-8 p-0" title="Map">
        <Map size={16} />
      </button>
      <button className="btn-ghost w-8 h-8 p-0" title="Upload">
        <Upload size={16} />
      </button>
      <button className="btn-ghost w-8 h-8 p-0" title="Save">
        <Download size={16} />
      </button>
      <button className="btn-ghost w-8 h-8 p-0" title="Zoom In">
        <ZoomIn size={16} />
      </button>
      <button className="btn-ghost w-8 h-8 p-0" title="Zoom Out">
        <ZoomOut size={16} />
      </button>
      <button className="btn-ghost w-8 h-8 p-0" title="Settings">
        <Settings size={16} />
      </button>
    </div>
  );

  return (
    <div className="battle-room-layout flex h-full min-h-0 w-full overflow-hidden flex-col">
      {/* Topbar with panel controls */}
      <Topbar
        toolbar={<div className="flex w-full min-w-0 items-center">{topTools}</div>}
        iconbar={topIconbar ?? defaultIconbar}
        onToggleLeftPanel={toggleLeftPanel}
        onToggleRightPanel={toggleRightPanel}
        leftPanelCollapsed={leftMode === 'collapsed'}
        rightPanelCollapsed={rightMode === 'collapsed'}
        leftPanelMode={leftMode}
        rightPanelMode={rightMode}
        compact={isCompactViewport}
      />

      {/* Main content area with sidebars */}
      <div className="battle-room-layout__content flex-1 min-h-0 flex overflow-hidden">
        {/* Left Sidebar */}
        <Sidebar
          id="left"
          icons={leftDock.mode === 'expanded' ? null : leftIcons}
          min={240}
          max={400}
          defaultWidth={300}
          resizable={true}
          mode={leftMode}
          onModeChange={isCompactViewport
            ? (mode) => setCompactDrawer(mode === 'collapsed' ? null : 'left')
            : leftDock.setMode}
          isCompact={isCompactViewport}
          onRequestClose={() => setCompactDrawer(null)}
          drawerTitle="Battle controls"
          className={`battle-room-layout__sidebar border-r flex-shrink-0 ${isCompactViewport ? 'battle-room-layout__mobile-drawer battle-room-layout__mobile-drawer--left' : ''}`}
        >
          <div className="flex flex-col gap-3 min-h-0">
            {leftPanel}
          </div>
        </Sidebar>

        {/* Main content - critical: allow stage to grow */}
        <main className="battle-room-layout__stage flex-1 min-w-0 flex flex-col overflow-hidden">
          <div className="flex-1 min-h-0">
            {children}
          </div>
        </main>

        {/* Right Sidebar */}
        <Sidebar
          id="right"
          icons={(
            <div className="flex flex-col gap-4 items-center p-2">
              {/* Character/Monster Sheet */}
              <button
                className="battle-room-layout__rail-button btn-ghost w-10 h-10 p-0 flex items-center justify-center rounded-lg transition-colors"
                title="Character Sheet"
                aria-label="Character Sheet"
                onClick={() => {
                  rightDock.setMode('expanded');
                  onRightIconAction && onRightIconAction('sheet');
                }}
              >
                <BookOpen size={20} className="text-base-content/80" />
              </button>

              {/* Combat Log */}
              <button
                className="battle-room-layout__rail-button btn-ghost w-10 h-10 p-0 flex items-center justify-center rounded-lg transition-colors"
                title="Combat Log"
                aria-label="Combat Log"
                onClick={() => {
                  rightDock.setMode('expanded');
                  onRightIconAction && onRightIconAction('log');
                }}
              >
                <Scroll size={20} className="text-base-content/80" />
              </button>

              {/* Chat */}
              <button
                className="battle-room-layout__rail-button btn-ghost w-10 h-10 p-0 flex items-center justify-center rounded-lg transition-colors"
                title="Chat"
                aria-label="Chat"
                onClick={() => {
                  rightDock.setMode('expanded');
                  onRightIconAction && onRightIconAction('chat');
                }}
              >
                <MessageSquare size={20} className="text-base-content/80" />
              </button>
            </div>
          )}
          min={240}
          max={460}
          defaultWidth={300}
          resizable={true}
          mode={rightMode}
          onModeChange={isCompactViewport
            ? (mode) => setCompactDrawer(mode === 'collapsed' ? null : 'right')
            : rightDock.setMode}
          isCompact={isCompactViewport}
          onRequestClose={() => setCompactDrawer(null)}
          drawerTitle="Table details"
          className={`battle-room-layout__sidebar border-l flex-shrink-0 ${isCompactViewport ? 'battle-room-layout__mobile-drawer battle-room-layout__mobile-drawer--right' : ''}`}
        >
          {rightPanel}
        </Sidebar>
      </div>
    </div>
  );
}
